import { Fragment } from 'react';
import type { Seat } from '../../../shared/domain.ts';
import type { PlayerPublic } from '../../../shared/protocol.ts';
import { Board } from './Board.tsx';
import { ChatPanel } from './ChatPanel.tsx';
import { StatusBar } from './StatusBar.tsx';
import { markerOf } from './presentation.ts';
import type { MatchView } from './useMatch.ts';

export interface MatchPageProps {
  readonly view: MatchView;
  readonly onDrop: (column: number) => void;
  readonly onResign: () => void;
  readonly onPlayAgain: () => void;
  /** A host with a queue still open goes back to it, not into matchmaking. */
  readonly playAgainLabel?: string;
  readonly onSay: (text: string) => void;
  readonly yourUserId: string | null;
  /** Opens the move-by-move review. Absent until the match has an id to review. */
  readonly onReview?: (() => void) | undefined;
}

function PlayerCard({
  player,
  seat,
  marker,
  isYou,
  isToMove,
}: {
  player: PlayerPublic;
  seat: Seat;
  marker: 'filled' | 'ring';
  isYou: boolean;
  isToMove: boolean;
}) {
  return (
    <div className={`player player--seat${seat}${isToMove ? ' player--active' : ''}`}>
      <span
        className={`player__disc player__disc--seat${seat} player__disc--${marker}`}
        aria-hidden="true"
      >
        <span className="disc__marker" />
      </span>
      <span className="player__identity">
        <span className="player__name">
          {player.displayName}
          {isYou ? <span className="player__you"> (you)</span> : null}
        </span>
        <span className="player__meta">
          <span className="player__rating">{player.rating}</span>
        </span>
      </span>
    </div>
  );
}

export function MatchPage({
  view,
  onDrop,
  onResign,
  onPlayAgain,
  playAgainLabel = 'Play again',
  onSay,
  yourUserId,
  onReview,
}: MatchPageProps) {
  const interactive =
    view.phase === 'playing' &&
    view.connection !== 'reconnecting' &&
    view.yourSeat !== null &&
    view.toMove === view.yourSeat &&
    view.pendingColumn === null;

  const seats = view.seats;
  const seatNumbers = view.spec.rules.seats;

  return (
    <section className="match" aria-label={`${view.spec.components.title} match`}>
      {seats ? (
        <header className="match__players">
          {seatNumbers.map((seat, position) => {
            const player = seats[seat];
            if (!player) return null;
            return (
              <Fragment key={seat}>
                {position > 0 ? (
                  <span className="match__versus" aria-hidden="true">
                    vs
                  </span>
                ) : null}
                <PlayerCard
                  player={player}
                  seat={seat}
                  marker={markerOf(view.spec, seat)}
                  isYou={view.yourSeat === seat}
                  isToMove={view.phase === 'playing' && view.toMove === seat}
                />
              </Fragment>
            );
          })}
        </header>
      ) : null}

      <StatusBar view={view} />

      <Board
        spec={view.spec}
        board={view.board}
        yourSeat={view.yourSeat}
        legalColumns={view.legalColumns}
        pendingColumn={view.pendingColumn}
        lastMoveIndex={view.lastMoveIndex}
        winningLine={view.winningLine}
        interactive={interactive}
        onDrop={onDrop}
      />

      <footer className="match__actions">
        {view.phase === 'playing' ? (
          <button type="button" className="button button--quiet" onClick={onResign}>
            Resign
          </button>
        ) : null}
        {view.phase === 'ended' ? (
          <button type="button" className="button button--primary" onClick={onPlayAgain}>
            {playAgainLabel}
          </button>
        ) : null}
        {/*
          Second, not first: the game just ended and the thing most people want
          is another one. The review is for the player who wants to know why.
        */}
        {view.phase === 'ended' && onReview ? (
          <button type="button" className="button button--secondary" onClick={onReview}>
            Review game
          </button>
        ) : null}
      </footer>

      {/*
        Chat sits below the board: the game is what people came for, and a
        conversation must never push the board off a phone screen. Only a dead
        socket disables it — an absent opponent still receives the backlog when
        they resync, so the message is not wasted.
      */}
      <ChatPanel
        messages={view.chat}
        yourUserId={yourUserId}
        onSend={onSay}
        disabled={view.connection === 'reconnecting'}
      />
    </section>
  );
}
